import { useState, useCallback, useEffect } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { OnboardingScreen } from './OnboardingScreen';
import { OnboardingDots } from './OnboardingDots';
import { Button } from '@/components/ui/button';
import { Globe, TrendingDown, MessageSquare, Camera, Users } from 'lucide-react';
import { SaveAnywhereMockup } from './mockups/SaveAnywhereMockup';
import { SmartPriceMockup } from './mockups/SmartPriceMockup';

import { PersonalStylistMockup } from './mockups/PersonalStylistMockup';
import { TryOnMockup } from './mockups/TryOnMockup';
import { SocialPollsMockup } from './mockups/SocialPollsMockup';


const SCREENS = [
  {
    icon: Globe,
    headline: 'Save from literally anywhere',
    subtext: 'Any store, any app, any link - if you can see it, you can save it and we\'ll make it shopable.',
    mockup: SaveAnywhereMockup,
  },
  {
    icon: TrendingDown,
    headline: 'Smart price intelligence',
    subtext: 'Price drops, restock alerts, and cheaper alternatives - all automatic.',
    mockup: SmartPriceMockup,
  },
  {
    icon: MessageSquare,
    headline: 'Your personal stylist',
    subtext: 'Chat to your AI stylist. Get recommendations like never before.',
    mockup: PersonalStylistMockup,
  },
  {
    icon: Camera,
    headline: 'See it on you',
    subtext: 'Try on clothes, create outfits mixing up to 6 items from different stores, change your hair - visualise any look.',
    mockup: TryOnMockup,
  },
  {
    icon: Users,
    headline: 'Better with friends',
    subtext: 'Share saves, vote on polls, and get a second opinion from your crew.',
    mockup: SocialPollsMockup,
  },
];

interface ValueShowcaseFlowProps {
  onComplete: () => void;
}

export function ValueShowcaseFlow({ onComplete }: ValueShowcaseFlowProps) {
  const [emblaRef, emblaApi] = useEmblaCarousel({ watchDrag: true });
  const [currentIndex, setCurrentIndex] = useState(0);

  const isLast = currentIndex === SCREENS.length - 1;

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setCurrentIndex(emblaApi.selectedScrollSnap());
    emblaApi.on('select', onSelect);
    return () => { emblaApi.off('select', onSelect); };
  }, [emblaApi]);

  const handleNext = useCallback(() => {
    if (isLast) {
      onComplete();
    } else {
      emblaApi?.scrollNext();
    }
  }, [emblaApi, isLast, onComplete]);

  return (
    <div className="h-[100dvh] w-full flex flex-col">
      {/* Dots at top */}
      <div className="pt-safe px-6 pt-4 pb-2 bg-background">
        <OnboardingDots currentStep={currentIndex} totalSteps={SCREENS.length} />
      </div>

      {/* Carousel */}
      <div className="flex-1 overflow-hidden" ref={emblaRef}>
        <div className="flex h-full">
          {SCREENS.map((screen, i) => (
            <div key={i} className="min-w-0 shrink-0 grow-0 basis-full h-full">
              <OnboardingScreen
                heroContent={<screen.mockup isActive={currentIndex === i} />}
                icon={screen.icon}
                headline={screen.headline}
                subtext={screen.subtext}
              >
                <Button
                  onClick={handleNext}
                  size="lg"
                  className="w-full text-base font-semibold h-14 rounded-2xl"
                >
                  {isLast ? "Let's Go" : 'Next'}
                </Button>
                <button
                  onClick={onComplete}
                  className="w-full text-center text-sm text-muted-foreground py-2 active:text-foreground transition-colors"
                >
                  Skip
                </button>
              </OnboardingScreen>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
